import { MessageSquare, CheckCircle2, AlertCircle, Mail, ExternalLink, Send } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export default function Feedback() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !category || !message) {
      toast({
        title: "Missing fields",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-feedback", {
        body: { name, email, category, message },
      });

      if (error) throw error;

      toast({
        title: "Feedback sent!",
        description: "Thank you for your feedback. We'll review it shortly.",
      });

      // Reset form
      setName("");
      setEmail("");
      setCategory("");
      setMessage("");
    } catch (error: any) {
      console.error("Error sending feedback:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">🎉 Prototype Live – We Need Your Input!</h1>
          <p className="text-xl text-muted-foreground mt-4 max-w-3xl mx-auto">
            We're thrilled to share that the prototype for our new web app is now live. <strong>Second Chance Connect helps people with criminal records find meaningful employment, stable housing, and practical support.</strong> Your real‑world feedback will directly influence the next development phase.
          </p>
        </div>

        <div className="prose space-y-8">
          <section className="section-card mb-8 hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-accent/10 p-3 rounded-full flex-shrink-0">
                <MessageSquare className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h2 className="mb-4 mt-0">We'd Love Your Feedback</h2>
                <p className="mb-4">
                  Please explore the prototype and let us know what you think about:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Usability</strong>
                      <p className="text-muted-foreground mt-1">Is navigation intuitive?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Design</strong>
                      <p className="text-muted-foreground mt-1">Does the visual style appeal to you?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Functionality</strong>
                      <p className="text-muted-foreground mt-1">Any bugs, errors, or confusing features?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Missing features</strong>
                      <p className="text-muted-foreground mt-1">What capabilities would you expect that are not yet present?</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-br from-accent/5 to-primary/5 p-8 rounded-xl border border-accent/20 mb-8 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-accent/20 p-3 rounded-full flex-shrink-0">
                <Send className="h-6 w-6 text-accent" />
              </div>
              <h2 className="mb-0 mt-0">Share Your Feedback</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="bg-background"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Your Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-background"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category">Feedback Category *</Label>
                <Select value={category} onValueChange={setCategory} required>
                  <SelectTrigger id="category" className="bg-background">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usability">Usability</SelectItem>
                    <SelectItem value="design">Design</SelectItem>
                    <SelectItem value="functionality">Functionality / Bugs</SelectItem>
                    <SelectItem value="feature-request">Missing Features</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Your Feedback *</Label>
                <Textarea
                  id="message"
                  placeholder="Please share your thoughts, suggestions, or any issues you've encountered..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={6}
                  className="bg-background resize-none"
                />
              </div>
              
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full md:w-auto bg-accent text-accent-foreground hover:bg-accent/90"
              >
                {isSubmitting ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-accent/20">
              <p className="text-sm text-muted-foreground">
                Your feedback will be sent to{" "}
                <a
                  href="mailto:admin@sccapp.co.uk"
                  className="text-accent hover:underline font-medium"
                >
                  admin@sccapp.co.uk
                </a>
              </p>
            </div>
          </section>

          <div className="section-card bg-gradient-to-br from-accent/5 to-primary/5 border-accent/30 shadow-[var(--shadow-elevated)]">
            <div className="flex items-start gap-4">
              <div className="bg-accent/20 p-3 rounded-full flex-shrink-0">
                <ExternalLink className="h-7 w-7 text-accent" />
              </div>
              <div>
                <p className="text-foreground text-lg leading-relaxed mb-4">
                  Thank you for helping us shape the future of this project.
                </p>
                <a
                  href="https://sccapp.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors font-semibold shadow-sm hover:shadow-md"
                >
                  <ExternalLink className="h-5 w-5" />
                  Access the prototype here
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
